// ============================================================
// Live-Aktualisierung (SPEC §11.2) — die Gegenrichtung zum Polling.
//
// Das Backend hört mit EINER Verbindung auf `LISTEN lp_aenderung` und gibt
// jede Änderung an alle offenen `GET /api/ereignisse` weiter (SSE). Hier
// steht die Gegenseite: ein `EventSource`, solange jemand angemeldet ist, und
// je Ereignis ein Entwerten im Zwischenspeicher.
//
// ---- Vier Entscheidungen ---------------------------------------------------
//
// 1. GEBÜNDELT, HÖCHSTENS ALLE 500 MS. Wer eine Grenze zeichnet, löst je
//    Speichervorgang mehrere Zeilenänderungen aus; wer einen Stapel Bilder
//    hochlädt, dutzende. Jedes Ereignis einzeln zu entwerten hieße, `/stand`
//    dutzende Male hintereinander zu holen. Die Schlüssel sammeln sich
//    deshalb in einer Menge und werden gemeinsam entwertet.
//
// 2. DAS 60-S-POLLING BLEIBT (`useStand`). Ein SSE-Strom kann an einem Proxy
//    hängen bleiben, ohne dass der Browser es merkt — dann steht der Strom
//    still und niemand sieht es. Das Polling ist das Netz darunter; es kostet
//    im Normalfall eine Anfrage je Minute, und das ist der Preis dafür, dass
//    ein hängender Strom nicht zu veralteten Zahlen führt.
//
// 3. KEIN EIGENES WIEDERVERBINDEN. `EventSource` verbindet von selbst neu.
//    Eine eigene Schleife daneben ergäbe zwei Ströme nach jedem Aussetzer.
//    Abgebaut wird nur beim Abmelden und beim Verlassen der Seite.
//
// 4. WAS NICHT GELESEN WERDEN KANN, WIRD VERWORFEN. Der Körper eines
//    Ereignisses ist JSON vom Server; ein unlesbarer Satz darf den Strom
//    nicht abreißen lassen, sondern nur diese eine Nachricht kosten.
// ============================================================
import { useEffect } from "react";
import { useQueryClient, type InvalidateQueryFilters, type QueryClient } from "@tanstack/react-query";
import { gelerntSchluessel } from "../api/abschluss";
import { ansichtenSchluessel } from "../api/ansichten";
import { COCKPIT_VERLAUF_TEIL } from "../api/cockpit";
import { aktuellesSchluessel, schluessel } from "../api/hooks";
import { startseiteSchluessel } from "../api/startseite";
import { vorlagenSchluessel } from "../api/vorlagen";
import { vorschauSchluessel } from "../api/vorschau";
import type { Ereignis } from "../api/types";
import { useAuthStore } from "../store/useAuthStore";

/** Der Pfad des Stroms. Dasselbe `/api`-Präfix wie in `api/client.ts`. */
const WEG = "/api/ereignisse";

/** Entscheidung 1: höchstens alle 500 ms wird entwertet. */
const BUENDEL_MS = 500;

/**
 * Die Tabellen, die „Aktuelles" berühren: die vier aus Migration 011 (§16.1) und
 * `bereich` aus 012 (§16.6).
 *
 * `bereich` steht hier und nicht bei `stand`, obwohl es ein Stammdatum ist: die
 * Bereichsliste hat eine eigene Route und liegt unter `["aktuelles","bereiche"]`,
 * also unter demselben Präfix. Entwertet wird bewusst das ganze Präfix und nicht
 * nur die Liste — ein gelöschter oder stillgelegter Bereich ändert auch, was in
 * den Feedseiten zu sehen und zu wählen ist.
 */
const AKTUELLES_TABELLEN = new Set(["beitrag", "kommentar", "reaktion", "beitrag_medium", "bereich"]);

/**
 * Woraus „Zuletzt im Haus" auf der Startseite rechnet (§32.3, `GET /start/zuletzt`):
 * Bild hochgeladen, Abgabe abgegeben, Projekt geändert, Beitrag im Projekt.
 * Die Tabelle `startseite` selbst hat bewusst KEINEN Auslöser (§32.2) — eine
 * umsortierte Kachel betrifft niemanden sonst. Und die Abzeichen hängen nicht
 * hier: die Meldungen tragen keine Person, jedes Klatschen irgendwo ließe jede
 * offene Startseite sie neu rechnen (api/startseite.ts, die Wache).
 */
const ZULETZT_TABELLEN = new Set(["projekt", "abgabe", "bild", "beitrag"]);

/**
 * Woraus die Standliste (§23, `["staende", monat]`) und das Blatt der
 * Geschäftsführung (§18.6, `["leitung"]`) rechnen: die Monatswerte und
 * Meldungen, das Auftragsbuch, Nachträge, die Marken, der Projektkopf (Name,
 * Honorar, Phase), die Abgaben (Meilenstein, Ampel „überfällig"), die
 * Konzeptbestandteile, Büro und Team (Filter, Leitung).
 *
 * Fehlerrunde 01.10.2026, #17: beide Schlüssel liegen NICHT unter `["stand"]`
 * oder `["projekt", id]` — sie wurden von keinem Ereignis getroffen. Weil
 * `refetchOnWindowFocus` aus ist und keine der beiden Abfragen ein Intervall
 * hat, blieb ein offenes Blatt auf dem alten Wert stehen, während in der Akte
 * gemeldet wurde. SPEC §18 verlangt `["leitung"]` für die Controllingtabellen
 * ausdrücklich. Der PRÄFIX `["staende"]` trifft jeden Monat im Zwischenspeicher.
 */
const CONTROLLING_TABELLEN = new Set([
  "stand_monat",
  "stand_meldung",
  "projekt_lph",
  "nachtrag",
  "richtwert",
  "projekt",
  "projekt_phase",
  "projekt_buero",
  "projekt_person",
  "abgabe",
  "konzept_teil",
]);

/**
 * §39.11: woraus das Projekt-Cockpit rechnet (`["cockpit", id]`, api/cockpit.ts) — und
 * zwar die Tabellen, die zeilenweise MIT Projektkennung melden: Ziel und Erfolgskriterien
 * (`projekt`), das Recht am Haken (`projekt_person`: wer leitet), Meilenstein und Verlauf
 * (`abgabe`), Puls und Verlauf (`termin`, `stand_meldung`), Verlauf (`bild`).
 *
 * Das Cockpit liegt NICHT unter `["projekt", id]` — die Zeile mit der Projektkennung
 * unten trifft es also nicht von selbst. E39.2 sah `["projekt", id, "cockpit"]` vor
 * („Präfix projekt reicht, prüfen"); geprüft: er reicht nicht, weil die Runde OHNE
 * Kennung meldet (nächster Absatz), und ein Präfix, der jedes Cockpit trifft, gibt es
 * unter `projekt` nur als `["projekt"]` — jedes Projekt in jedem Zwischenspeicher.
 *
 * `runde` und `runde_punkt` melden ANWEISUNGSWEISE und ohne Kennung (031): sie treffen
 * darum JEDES Cockpit (`["cockpit"]`). Das sind im Zwischenspeicher eines Geräts eine
 * Handvoll Abfragen, und die des gerade offenen Projekts ist die einzige, die wirklich
 * neu holt (die anderen sind nicht eingehängt) — den VERLAUF aber nimmt `entwertungFuer`
 * dabei aus (§39.11). `aenderung_log` hat keinen Auslöser; was dort landet, kommt mit der
 * `projekt`- oder `bild`-Meldung derselben Änderung.
 *
 * §50.11: `termin_punkt` (044) meldet zeilenweise MIT Projektkennung — Offen (Aufgaben, Klärungen),
 * Beschlüsse und der Verlauf („1 Beschluss, 2 Aufgaben") rechnen daraus.
 *
 * §51.10: `projekt_frist` (046) — eine offene Frist ist ein Kandidat für den Meilenstein.
 */
const COCKPIT_TABELLEN = new Set([
  "projekt",
  "projekt_person",
  "abgabe",
  "termin",
  "termin_punkt",
  "stand_meldung",
  "bild",
  "projekt_frist",
]);

/**
 * §52.10: woraus die Vorschau rechnet (`["vorschau", …]`, api/vorschau.ts) — die Abgaben und
 * Termine selbst, das Projekt (Freigabe, Löschen, Name/Kürzel/Nummer in der Zellenliste), die
 * Zuordnung zum Büro (`projekt_buero`: welches federführend ist) und das Büro (Name, Farbe,
 * `sortierung` der Zeilen; ein neues Büro ist eine neue Zeile).
 *
 * Gezielt nach Projekt geht es nicht: eine Zelle rechnet aus vielen Projekten, und eine
 * Meldung sagt nicht, in welcher Woche und bei welchem Büro sie landet. Darum der Präfix
 * `["vorschau"]` — Raster und jede geöffnete Zelle. Das sind im Zwischenspeicher eines
 * Geräts eine Handvoll Abfragen; neu geholt werden nur die eingehängten.
 *
 * §51.10: `projekt_frist` (046) — die Fristen nach Abnahme über die Erweiterungsstelle §52.8.
 */
const VORSCHAU_TABELLEN = new Set(["abgabe", "termin", "projekt", "projekt_buero", "buero", "projekt_frist"]);

/**
 * §51.10: woraus die hausweite Sammlung „Gelernt" rechnet (`["gelernt"]`, api/abschluss.ts): die
 * Rückblicke selbst und das Projekt (Name, Kürzel, Team, Freigabe, Löschen). Die übrigen Tabellen
 * aus §51 (`projekt_frist`, `plan`, `plan_versand`, `projekt_abschluss`, `kostenstand`,
 * `rueckblick`) melden MIT Projektkennung und treffen ihre Listen über `["projekt", id]` von selbst.
 */
const GELERNT_TABELLEN = new Set(["rueckblick", "projekt"]);

/** Ein Schlüssel als Zeichenkette — zum Entdoppeln in der Menge. */
type SchluesselWort = string;

function schluesselAus(wort: SchluesselWort): readonly unknown[] {
  return JSON.parse(wort) as readonly unknown[];
}

/**
 * Welche Schlüssel entwertet EIN Ereignis?
 *
 * `stand` immer: er trägt Projekte, Büros, Phasen, Personen und die offenen
 * Abgaben in EINER Antwort (SPEC §3) — fast jede Änderung im Haus steht darin.
 * Alles Weitere hängt an der Tabelle, so wie `api/types.ts` es beschreibt.
 */
export function schluesselFuer(e: Ereignis): SchluesselWort[] {
  const keys: readonly unknown[][] = [
    [...schluessel.stand],
    // Die Zeitleiste liegt unter ["abgaben", von, bis] — der PRÄFIX trifft
    // jedes Fenster, ein ausgeschriebener Schlüssel nur eines.
    ...(e.tabelle === "abgabe" ? [[...schluessel.abgabenAlle]] : []),
    // §40.2: dieselbe Regel für die Termine — Zeitleiste und Leitungsblatt. §50.11: auch bei
    // einem Termin-Punkt, denn jede Terminliste trägt `punkte_zahl`. Die Punkteliste EINES
    // Termins liegt unter `["projekt", id, …]` (api/terminpunkte.ts) und kommt mit der
    // Projektkennung in der Zeile darunter.
    ...(e.tabelle === "termin" || e.tabelle === "termin_punkt" ? [[...schluessel.termineAlle]] : []),
    ...(e.projekt_id ? [[...schluessel.projekt(e.projekt_id)]] : []),
    // Eine geänderte Person steht in der Verwaltungsliste UND in `['ich']`:
    // wer sich gerade selbst umbenennt, sieht es sonst in der Kopfleiste erst
    // beim nächsten Neuladen.
    // `user_buero` (§14.2) steht daneben und nicht darunter: ein PATCH, der NUR
    // die Zweitbüros ändert, fasst die `users`-Zeile gar nicht an — es käme
    // also keine 'users'-Meldung, und die Verwaltungsliste bliebe bei der
    // alten Menge stehen.
    // §36: auch die Freigaben — `warten` hängt an der Rolle der fragenden Person
    // (nur wer freigeben kann, hat dort etwas; seit dem 29.09.2026 nicht mehr am
    // Büro). Wer zur Projektleitung wird oder zurückgestuft, sähe sonst die alte
    // Liste und den alten Punkt, bis zufällig ein Projekt sich regt.
    ...(e.tabelle === "users" || e.tabelle === "user_buero"
      ? [[...schluessel.adminNutzer], [...schluessel.ich], [...schluessel.freigaben]]
      : []),
    // Aktuelles (§16.1, §16.6): fünf Tabellen, EIN Präfix. `["aktuelles"]` trifft
    // die Feedseiten, den einzelnen Beitrag, die Bereichsliste UND die Zählung für den Ungelesen-
    // Punkt — ein ausgeschriebener Listenschlüssel träfe nur eine
    // Filterfassung, und wer gerade nach „Pflanzlisten" filtert, sähe den
    // neuen Beitrag erst beim nächsten Neuladen.
    ...(AKTUELLES_TABELLEN.has(e.tabelle) ? [[...aktuellesSchluessel.alle]] : []),
    // Die Richtwerte (015, §18.2) sind die einzige Controllingtabelle OHNE
    // Projektbezug: ihr Auslöser ist anweisungsweise, `projekt_id` bleibt `null`,
    // und die Zeile darüber greift nicht. Für die LEITER eines Projekts
    // (`["projekt", id, "stand"]`) reicht bei `projekt_lph`, `stand_meldung` und
    // `nachtrag` die Projektzeile oben; Standliste und Leitungsblatt liegen
    // woanders und kommen in der Zeile darunter dazu (`CONTROLLING_TABELLEN`).
    ...(e.tabelle === "richtwert" ? [[...schluessel.richtwerte]] : []),
    ...(CONTROLLING_TABELLEN.has(e.tabelle) ? [[...schluessel.staendeAlle], [...schluessel.leitung]] : []),
    // Wünsche und Stimmen (§21): beide entwerten dieselbe Liste — eine Stimme
    // kann einen Wunsch nach oben schieben, also reicht es nicht, die eine
    // Zeile nachzuladen.
    ...(e.tabelle === "wunsch" || e.tabelle === "wunsch_stimme"
      ? [[...schluessel.wuensche]]
      : []),
    // Die Runde (§18.5): ihre Auslöser gibt es seit 031, eine Zuordnung fehlte
    // bis §32 — die Rundenkarte der Startseite und `/runde` wurden erst nach
    // 30 s UND einem Neuaufbau frisch. EIN Präfix trifft die Liste, die
    // einzelne Runde und „meine".
    ...(e.tabelle === "runde" || e.tabelle === "runde_punkt"
      ? [[...schluessel.rundenAlle]]
      : []),
    // §39.11: das Cockpit — gezielt, wo die Meldung eine Projektkennung trägt, und
    // jedes, wenn die Runde sich regt (sie meldet ohne Kennung).
    ...(e.projekt_id && COCKPIT_TABELLEN.has(e.tabelle) ? [[...schluessel.cockpit(e.projekt_id)]] : []),
    ...(e.tabelle === "runde" || e.tabelle === "runde_punkt" ? [[...schluessel.cockpitAlle]] : []),
    // §52.10: die Vorschau — immer der ganze Präfix (siehe `VORSCHAU_TABELLEN`).
    ...(VORSCHAU_TABELLEN.has(e.tabelle) ? [[...vorschauSchluessel.alle]] : []),
    ...(ZULETZT_TABELLEN.has(e.tabelle) ? [[...startseiteSchluessel.zuletzt]] : []),
    // §36: die Freigaben. `projekt` trägt jeden Zustandswechsel (anlegen,
    // freigeben, ablehnen, erneut einreichen, löschen) und das Feld
    // `leitung_id`; `projekt_person`, weil seit dem 29.09.2026 die Projektleitung
    // DES PROJEKTS entscheidet, bei wem eine Einreichung in `warten` steht — und
    // eine Teamzeile `leitung` zählt dazu (wandert die Anfrage, verliert die alte
    // Leitung ihre Zeile). `projekt_buero` bleibt dabei: die Startkarte zeigt das
    // federführende Büro je Zeile. Die Meldung trägt nur die Kennung (006) — ob das
    // Projekt jemanden etwas angeht, rechnet der Server beim Nachladen, nicht
    // dieser Strom.
    ...(e.tabelle === "projekt" || e.tabelle === "projekt_person" || e.tabelle === "projekt_buero"
      ? [[...schluessel.freigaben]]
      : []),
    // §37 (041): die Ansichten EINES Projekts liegen unter `["projekt", id]` und
    // kommen oben über die Projektkennung mit. Die hausweite Reihe der Startseite
    // und die einzelne Ansicht (Link) liegen unter `["ansichten"]` — ohne diese
    // Zeile stünde dort eine gelöschte Ansicht bis zum Neuladen. `projekt` steht
    // dabei, weil ein (weich) gelöschtes Projekt seine Ansichten aus der Reihe
    // nimmt und ein abgelehntes sie für die meisten unsichtbar macht — beides
    // rechnet der Server beim Nachladen, die Meldung trägt nur die Kennung.
    ...(e.tabelle === "projekt_ansicht" || e.tabelle === "projekt"
      ? [[...ansichtenSchluessel.alle]]
      : []),
    // §50.11: die Projektvorlagen (044) melden anweisungsweise, ohne Projektkennung — EINE Liste
    // für Verwaltung und Anlegeformular.
    ...(e.tabelle === "projekt_vorlage" ? [[...vorlagenSchluessel.alle]] : []),
    // §51.10: „Gelernt" (046) — hausweit, ohne Projektkennung im Schlüssel.
    ...(GELERNT_TABELLEN.has(e.tabelle) ? [[...gelerntSchluessel.alle]] : []),
  ];
  // `buero` und `phase` haben keine eigene Leseroute — sie stehen in `stand`,
  // der oben ohnehin dabei ist (api/hooks.ts, „Verwaltung: Büros und Phasen").
  return keys.map((k) => JSON.stringify(k));
}

/** `["cockpit"]` als Wort — der eine Schlüssel, den `entwertungFuer` anders behandelt. */
const COCKPIT_ALLE_WORT: SchluesselWort = JSON.stringify(schluessel.cockpitAlle);

/**
 * Womit `leeren` EIN gesammeltes Wort entwertet. Fast immer genau dieser Präfix.
 *
 * Die Ausnahme ist `["cockpit"]` (§39.11): er kommt nur aus Rundenmeldungen, und eine
 * laufende Runde meldet bei JEDEM verlassenen Feld. Gebündelt alle 500 ms träfe er sonst
 * auch jeden eingehängten `useCockpitVerlauf` auf jedem Gerät — TanStack holte jede
 * geladene Seite neu, und jede rechnet den Strom aus sechs Quellen. Darum lässt der
 * Filter den Verlauf (`["cockpit", id, COCKPIT_VERLAUF_TEIL]`) aus: Kopf (samt
 * `verlauf_zahl`), Offen und Beschlüsse kommen weiter sofort, der Verlauf ist
 * eingeklappt die Vorgabe und wird beim nächsten Aufklappen nach `FRISCH_MS` frisch
 * geholt. Meldungen MIT Projektkennung (`["cockpit", id]`) und die Haken der Runde und
 * des Cockpits entwerten weiterhin alles.
 */
export function entwertungFuer(wort: SchluesselWort): InvalidateQueryFilters {
  const queryKey = schluesselAus(wort);
  if (wort === COCKPIT_ALLE_WORT) {
    return { queryKey, predicate: (q) => q.queryKey[2] !== COCKPIT_VERLAUF_TEIL };
  }
  return { queryKey };
}

/**
 * Den Strom öffnen. Gibt die Abbaufunktion zurück; mehrfaches Aufrufen ist
 * Sache des Aufrufers (der Haken unten ruft genau einmal je Anmeldung).
 */
export function ereignisseStarten(qc: QueryClient): () => void {
  if (typeof window === "undefined" || typeof window.EventSource === "undefined") {
    // Ohne EventSource (alte Umgebung, Testlauf) bleibt das 60-s-Polling —
    // die Anwendung ist dann langsamer, aber nicht kaputt.
    return () => {};
  }

  const quelle = new EventSource(WEG, { withCredentials: true });
  const offen = new Set<SchluesselWort>();
  let takt: number | null = null;
  let abgebaut = false;

  const leeren = (): void => {
    takt = null;
    if (abgebaut || offen.size === 0) return;
    const jetzt = [...offen];
    offen.clear();
    for (const wort of jetzt) void qc.invalidateQueries(entwertungFuer(wort));
  };

  const merken = (e: Ereignis): void => {
    for (const wort of schluesselFuer(e)) offen.add(wort);
    if (takt !== null) return;
    takt = window.setTimeout(leeren, BUENDEL_MS);
  };

  const aufAenderung = (ev: MessageEvent<string>): void => {
    try {
      const daten = JSON.parse(ev.data) as Ereignis;
      // Entscheidung 4: nur was wie ein Ereignis aussieht, zählt als eines.
      if (!daten || typeof daten.tabelle !== "string") return;
      merken(daten);
    } catch {
      // Ein unlesbarer Satz kostet diese eine Nachricht und nicht den Strom.
    }
  };

  quelle.addEventListener("aenderung", aufAenderung as EventListener);

  return () => {
    abgebaut = true;
    if (takt !== null) window.clearTimeout(takt);
    quelle.removeEventListener("aenderung", aufAenderung as EventListener);
    quelle.close();
  };
}

/**
 * Der Strom, an den Anmeldestatus gehängt — EINMAL in der ganzen Anwendung,
 * aus `App.tsx` (`Wurzel`). Zwei Ströme wären zwei Verbindungen je Reiter, und
 * das Backend hält sie beide offen.
 */
export function useEreignisse(): void {
  const qc = useQueryClient();
  const status = useAuthStore((s) => s.status);

  useEffect(() => {
    if (status !== "angemeldet") return;
    return ereignisseStarten(qc);
  }, [status, qc]);
}
